/**
 * Synthesised comic SFX recipes (Web Audio, no files). Each recipe schedules nodes on `ctx`
 * starting at `t0` and routes them into `out`.
 */

export type SoundName =
  | 'bid'
  | 'tick'
  | 'gavel'
  | 'reveal'
  | 'pass'
  | 'win'
  | 'lose'
  | 'reaction'
  | 'join'
  | 'click'
  | 'slam';

export const SOUND_NAMES: readonly SoundName[] = [
  'bid',
  'tick',
  'gavel',
  'reveal',
  'pass',
  'win',
  'lose',
  'reaction',
  'join',
  'click',
  'slam',
];

type Recipe = (ctx: BaseAudioContext, out: AudioNode, t0: number) => void;

const noiseCache = new WeakMap<BaseAudioContext, AudioBuffer>();

function noiseBuffer(ctx: BaseAudioContext): AudioBuffer {
  let buf = noiseCache.get(ctx);
  if (!buf) {
    buf = ctx.createBuffer(1, Math.floor(ctx.sampleRate * 1.5), ctx.sampleRate);
    const data = buf.getChannelData(0);
    for (let i = 0; i < data.length; i++) data[i] = Math.random() * 2 - 1;
    noiseCache.set(ctx, buf);
  }
  return buf;
}

/** Attack/decay gain envelope. */
function env(
  ctx: BaseAudioContext,
  out: AudioNode,
  t: number,
  peak: number,
  attack: number,
  decay: number,
): GainNode {
  const g = ctx.createGain();
  g.gain.setValueAtTime(0.0001, t);
  g.gain.exponentialRampToValueAtTime(Math.max(0.0002, peak), t + attack);
  g.gain.exponentialRampToValueAtTime(0.0001, t + attack + decay);
  g.connect(out);
  return g;
}

function tone(
  ctx: BaseAudioContext,
  out: AudioNode,
  t: number,
  o: {
    type?: OscillatorType;
    f0: number;
    f1?: number;
    dur: number;
    peak?: number;
    attack?: number;
    glide?: number;
    detune?: number;
  },
) {
  const osc = ctx.createOscillator();
  osc.type = o.type ?? 'sine';
  osc.frequency.setValueAtTime(o.f0, t);
  if (o.f1) osc.frequency.exponentialRampToValueAtTime(o.f1, t + (o.glide ?? o.dur));
  if (o.detune) osc.detune.setValueAtTime(o.detune, t);
  const g = env(ctx, out, t, o.peak ?? 0.5, o.attack ?? 0.005, o.dur);
  osc.connect(g);
  osc.start(t);
  osc.stop(t + (o.attack ?? 0.005) + o.dur + 0.05);
}

function noise(
  ctx: BaseAudioContext,
  out: AudioNode,
  t: number,
  o: {
    dur: number;
    peak?: number;
    type?: BiquadFilterType;
    f0: number;
    f1?: number;
    q?: number;
    attack?: number;
  },
) {
  const src = ctx.createBufferSource();
  src.buffer = noiseBuffer(ctx);
  const filter = ctx.createBiquadFilter();
  filter.type = o.type ?? 'bandpass';
  filter.Q.value = o.q ?? 1;
  filter.frequency.setValueAtTime(o.f0, t);
  if (o.f1) filter.frequency.exponentialRampToValueAtTime(o.f1, t + o.dur);
  const g = env(ctx, out, t, o.peak ?? 0.4, o.attack ?? 0.003, o.dur);
  src.connect(filter).connect(g);
  src.start(t, Math.random() * 0.5);
  src.stop(t + (o.attack ?? 0.003) + o.dur + 0.05);
}

const NOTE = (semisFromA4: number) => 440 * Math.pow(2, semisFromA4 / 12);
const C5 = NOTE(3);
const E5 = NOTE(7);
const G5 = NOTE(10);
const C6 = NOTE(15);

export const RECIPES: Record<SoundName, Recipe> = {
  /** Bubbly "pop" for a bid. */
  bid: (ctx, out, t) => {
    tone(ctx, out, t, { f0: 260, f1: 980, dur: 0.11, glide: 0.06, peak: 0.55 });
    tone(ctx, out, t + 0.012, { type: 'triangle', f0: 1400, f1: 2200, dur: 0.05, peak: 0.12 });
  },

  /** Wood-block tick for the last 3 seconds. */
  tick: (ctx, out, t) => {
    tone(ctx, out, t, {
      type: 'triangle',
      f0: 1750,
      f1: 1500,
      dur: 0.06,
      peak: 0.45,
      attack: 0.002,
    });
    noise(ctx, out, t, { type: 'highpass', f0: 3500, dur: 0.025, peak: 0.12 });
  },

  /** Double gavel knock: SATILDI! */
  gavel: (ctx, out, t) => {
    const knock = (at: number, peak: number) => {
      tone(ctx, out, at, { f0: 190, f1: 60, dur: 0.22, glide: 0.12, peak });
      noise(ctx, out, at, { type: 'lowpass', f0: 1800, f1: 400, dur: 0.12, peak: peak * 0.8 });
      tone(ctx, out, at, { type: 'square', f0: 620, f1: 380, dur: 0.035, peak: peak * 0.18 });
    };
    knock(t, 0.75);
    knock(t + 0.17, 0.95);
  },

  /** Snare drum roll crescendo + major chord sting. */
  reveal: (ctx, out, t) => {
    const rollDur = 0.85;
    let at = t;
    let gap = 0.07;
    let i = 0;
    while (at < t + rollDur) {
      const p = 0.08 + (0.32 * (at - t)) / rollDur;
      noise(ctx, out, at, {
        type: 'bandpass',
        f0: 1900 + (i % 2) * 300,
        q: 0.7,
        dur: 0.06,
        peak: p,
      });
      at += gap;
      gap = Math.max(0.028, gap * 0.9);
      i++;
    }
    const s = t + rollDur + 0.02;
    noise(ctx, out, s, { type: 'lowpass', f0: 5000, f1: 800, dur: 0.5, peak: 0.35 }); // crash
    tone(ctx, out, s, { f0: 110, f1: 55, dur: 0.3, peak: 0.6 }); // kick
    for (const f of [C5, E5, G5, C6])
      tone(ctx, out, s, { type: 'sawtooth', f0: f, dur: 0.55, peak: 0.07, attack: 0.01 });
    for (const f of [C5, G5])
      tone(ctx, out, s, { type: 'square', f0: f / 2, dur: 0.45, peak: 0.05, attack: 0.01 });
  },

  /** PAS whoosh. */
  pass: (ctx, out, t) => {
    noise(ctx, out, t, {
      type: 'bandpass',
      f0: 380,
      f1: 3200,
      q: 1.6,
      dur: 0.18,
      peak: 0.45,
      attack: 0.06,
    });
    noise(ctx, out, t + 0.16, {
      type: 'bandpass',
      f0: 3200,
      f1: 260,
      q: 1.4,
      dur: 0.22,
      peak: 0.25,
    });
  },

  /** Victory fanfare. */
  win: (ctx, out, t) => {
    const seq: Array<[number, number, number]> = [
      [C5, 0, 0.12],
      [E5, 0.12, 0.12],
      [G5, 0.24, 0.12],
      [C6, 0.36, 0.22],
      [G5, 0.6, 0.1],
      [C6, 0.72, 0.65],
    ];
    for (const [f, dt, d] of seq) {
      tone(ctx, out, t + dt, { type: 'square', f0: f, dur: d, peak: 0.12, attack: 0.008 });
      tone(ctx, out, t + dt, { type: 'triangle', f0: f * 2, dur: d * 0.8, peak: 0.08 });
    }
    for (const f of [C5, E5, G5])
      tone(ctx, out, t + 0.72, { type: 'sawtooth', f0: f / 2, dur: 0.7, peak: 0.05, attack: 0.02 });
    noise(ctx, out, t + 0.72, { type: 'highpass', f0: 6000, dur: 0.5, peak: 0.12 });
  },

  /** Sad trombone: wah wah wah waaah. */
  lose: (ctx, out, t) => {
    const notes = [NOTE(-2), NOTE(-3), NOTE(-4)];
    notes.forEach((f, i) => {
      tone(ctx, out, t + i * 0.32, {
        type: 'sawtooth',
        f0: f,
        f1: f * 0.97,
        dur: 0.28,
        peak: 0.16,
        attack: 0.03,
      });
    });
    const last = t + 0.96;
    const osc = ctx.createOscillator();
    osc.type = 'sawtooth';
    osc.frequency.setValueAtTime(NOTE(-5), last);
    osc.frequency.linearRampToValueAtTime(NOTE(-6.5), last + 0.9);
    const lfo = ctx.createOscillator();
    const lfoGain = ctx.createGain();
    lfo.frequency.value = 6;
    lfoGain.gain.value = 9;
    lfo.connect(lfoGain).connect(osc.frequency);
    const lp = ctx.createBiquadFilter();
    lp.type = 'lowpass';
    lp.frequency.value = 1100;
    const g = env(ctx, out, last, 0.18, 0.04, 0.95);
    osc.connect(lp).connect(g);
    osc.start(last);
    lfo.start(last);
    osc.stop(last + 1.05);
    lfo.stop(last + 1.05);
  },

  /** Light bubble pop for reactions (slightly random pitch). */
  reaction: (ctx, out, t) => {
    const f = 520 + Math.random() * 260;
    tone(ctx, out, t, { f0: f, f1: f * 2.4, dur: 0.08, glide: 0.05, peak: 0.3 });
  },

  /** Two-note "someone joined" blip. */
  join: (ctx, out, t) => {
    tone(ctx, out, t, { type: 'triangle', f0: E5, dur: 0.09, peak: 0.3 });
    tone(ctx, out, t + 0.09, { type: 'triangle', f0: NOTE(14), dur: 0.16, peak: 0.3 });
  },

  /** UI click. */
  click: (ctx, out, t) => {
    tone(ctx, out, t, { type: 'square', f0: 900, f1: 600, dur: 0.03, peak: 0.1, attack: 0.001 });
  },

  /** Low boom for VS / big slams. */
  slam: (ctx, out, t) => {
    tone(ctx, out, t, { f0: 150, f1: 38, dur: 0.5, glide: 0.35, peak: 0.85 });
    noise(ctx, out, t, { type: 'lowpass', f0: 2600, f1: 200, dur: 0.35, peak: 0.5 });
  },
};
